import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IAttribute } from "@/data/model/Attribute";
import { AttributeDisplayType, AttributeEffect } from "@/data/model/Enums";
import { CurrentLocationService } from "@/data/services/CurrentLocationService";
import { Format } from "@/data/util/Format";



import { hasAttributeDisplayType, splitIconClasses } from "@/shared/helpers/AttributeHelper";
import { translate } from "@/shared/localization/language";



import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";


export class AttributeTableController {
    private $element: JQuery;
    private dao: CatalogDao;
    private dataTable: DataTable<IAttribute>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<CatalogDao>(CatalogDao);

        const tableConfig: IDataTableConfig<IAttribute> = {
            columnMapping: [
                {
                    name: i18n("general.fields.designation"),
                    property: "name",
                    formatter: (m) => Format.htmlEscape(translate(m.name)),
                },
                {
                    name: i18n("attribute.fields.displayAs"),
                    property: "displayType",
                    styles: { width: "15%" },
                    formatter: (m, p) => AttributeTableController.formatDisplayType(p),
                },
                {
                    name: i18n("attribute.fields.icon"),
                    property: "iconClass",
                    sortable: false,
                    styles: { width: "8%" },
                    // a preview of the icons themselves, the classes are set instead of rendered
                    // as markup because they are free text from the form
                    formatter: (m, p) => AttributeTableController.formatIcons(p),
                },
                {
                    name: i18n("attribute.fields.filter"),
                    property: "effect",
                    styles: { width: "15%" },
                    formatter: (m, p) => (p == null ? "" : i18n("enums.AttributeEffect." + AttributeEffect[p])),
                },
                {
                    name: i18n("attribute.fields.type"),
                    property: "isCustom",
                    styles: { width: "10%" },
                    formatter: (m, p) =>
                        p === true ? i18n("attribute.types.custom") : i18n("attribute.types.standard"),
                },
                {
                    name: i18n("general.fields.sortOrder"),
                    property: "sortOrder",
                    styles: { width: "8%" },
                    formatter: (m, p) => (p == null ? "" : p),
                },
                {
                    name: i18n("attribute.fields.isEnabled"),
                    property: "isEnabled",
                    styles: { width: "8%" },
                    formatter: (m, p) => (p === true ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: "",
                    property: "id",
                    sortable: false,
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: false,
            locationDependend: true,
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.dataTable = new DataTable<IAttribute>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.attributeDataSource,
            tableConfig
        );

        this.dataTable.onClick(
            (m) => {
                location.href = AttributeTableController.editUrl(m);
            },
            (m) => {
                window.open(AttributeTableController.editUrl(m));
            }
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(() => {
            this.dataTable.loadData(true);
        }, this);
    }

    // a standard attribute belongs to no location, so the edit page has to be told which one it is opened from
    private static editUrl(attribute: IAttribute): string {
        const locationId = CurrentLocationService.getLocationId();
        if (attribute.isCustom || locationId === undefined) return "/attribute/" + attribute.id;

        return "/attribute/" + attribute.id + "?lId=" + locationId;
    }

    private static formatDisplayType(displayType: AttributeDisplayType): string {
        return [AttributeDisplayType.Icon, AttributeDisplayType.Flag, AttributeDisplayType.Filter]
            .filter((flag) => hasAttributeDisplayType(displayType, flag))
            .map((flag) => i18n("enums.AttributeDisplayType." + AttributeDisplayType[flag]))
            .join(", ");
    }

    private static formatIcons(iconClass: string | undefined): JQuery | string {
        const classes = splitIconClasses(iconClass);
        if (classes.length === 0) return "";

        const $icons = $('<span class="item-icons"></span>');
        const $icon = $('<span class="item-icon"></span>').appendTo($icons);
        for (const iconClasses of classes) $("<i></i>").addClass(iconClasses).appendTo($icon);

        return $icons;
    }
}
